import { Button } from '@/components/ui/button';
import { DatePickerWithRange } from '@/components/ui/date-picker/date-range-picker';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { SearchIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchemaFilter = z.object({
  projectId: z.string().optional(),
  operation: z.array(z.string()).optional(),
  limit: z.string().optional(),
  date: z
    .object({
      from: z.date().optional(),
      to: z.date().optional(),
    })
    .optional(),
});

export type StorageLogFilterSchema = z.infer<typeof formSchemaFilter>;

const operationOptions = [
  { value: 'upload', label: 'Upload' },
  { value: 'download', label: 'Download' },
  { value: 'delete', label: 'Delete' },
  { value: 'copy', label: 'Copy' },
  { value: 'move', label: 'Move' },
  { value: 'list', label: 'List' },
];

const limitOptions = [
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: '500', label: '500' },
];

type Props = {
  onSearch: (data: StorageLogFilterSchema) => void;
  projects?: { id: string; name: string }[];
  showProjectFilter?: boolean;
};

const StorageLogSearch = ({ onSearch, projects, showProjectFilter = true }: Props) => {
  const form = useForm<StorageLogFilterSchema>({
    resolver: zodResolver(formSchemaFilter),
    defaultValues: {
      projectId: '',
      operation: [],
      limit: '100',
    },
  });

  const onSubmit = async (data: StorageLogFilterSchema) => {
    onSearch(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 lg:flex-row">
          {showProjectFilter && (
            <div className="flex-1">
              <FormField
                control={form.control}
                name="projectId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Search by Project ID"
                        startIcon={
                          <SearchIcon className="h-4 w-4 text-gray-400" />
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <FormField
              control={form.control}
              name="operation"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <MultiSelect
                      options={operationOptions}
                      value={field.value || []}
                      onValueChange={field.onChange}
                      placeholder="Operation"
                      maxCount={1}
                      compact
                      className="w-[180px]"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="limit"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Limit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {limitOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <DatePickerWithRange
                      className="w-[250px]"
                      value={field.value as DateRange}
                      onChange={field.onChange}
                      placeholder="Filter Date"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button variant="secondary" type="submit">
              Filter
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default StorageLogSearch;
